import type { InputHTMLAttributes } from "react";
import classNames from "classnames";
import {
    CheckGlyph,
    IndeterminateGlyph,
} from "@design-system/Checkbox/CheckboxGlyphs";
import styles from "./NativeCheckbox.module.scss";

interface NativeCheckboxProps extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "type" | "checked" | "defaultChecked" | "className" | "children"
> {
    checked: boolean;
    /** Mixed state; set through the DOM property, which has no HTML attribute. */
    indeterminate?: boolean;
    /** Shown on hover by the table's `CellTooltipHost`, e.g. a disabled reason. */
    tooltipText?: string;
    className?: string;
}

/**
 * The design-system Checkbox look on a plain `<input type="checkbox">`: no
 * Base UI store or hooks, for controls repeated in every row. Use the
 * design-system Checkbox everywhere else.
 */
export const NativeCheckbox = ({
    checked,
    indeterminate = false,
    tooltipText,
    className,
    ...inputProps
}: Readonly<NativeCheckboxProps>) => (
    <span
        className={classNames(styles.root, className)}
        data-cell-tooltip={tooltipText}
        data-cell-tooltip-always={tooltipText === undefined ? undefined : ""}
    >
        <input
            {...inputProps}
            ref={(element) => {
                if (element) {
                    element.indeterminate = indeterminate;
                }
            }}
            type="checkbox"
            className={styles.input}
            checked={checked}
        />
        <CheckGlyph className={classNames(styles.glyph, styles.checkGlyph)} />
        <IndeterminateGlyph
            className={classNames(styles.glyph, styles.indeterminateGlyph)}
        />
    </span>
);
